/**
 * Deterministic pathfinding on a fine Cartesian navigation lattice. The visual
 * hex grid supplies terrain only; buildings, props, and ramparts are continuous
 * sub-tile obstacles. Returned waypoints are world-space points and are
 * string-pulled against the same geometry used by movement, so travel is not
 * constrained to visual tile centres or eight movement directions.
 */
import type { World } from "./world";
import type { Vec2 } from "./types";
import type { MoveDomain } from "./naval";
import { hexDistance } from "./hex";
import {
  RAMPART_PATH_MARGIN_RATIO,
  pointSegmentDistance,
  rampartGeometry,
  rampartSegmentBlocked,
} from "./ramparts";

/** Extra A* cost to route a tile occupied by a resource prop, scaled to how much
 *  SPACE the object takes: a thin trunk/bush barely deflects a path (units weave
 *  right past), a chunky rock/gold gets clearly routed around. Dense forests are
 *  still crossed (every option is penalised equally). Populated per-hex into
 *  world.propCost at spawn so A* just reads it. */
export function propTileCost(defId: string): number {
  switch (defId) {
    case "stone_rock": return 2.6;
    case "gold_ore": return 2.2;
    case "berry_bush": return 0.5;
    case "tree": return 0.7;
    default: return 1.0;
  }
}

/** A* routing penalty for stepping through a BUILDING tile. High so units route
 *  AROUND buildings normally, but finite so they WILL squeeze through a gap
 *  between small buildings when that's the only way (a whole-tile wall would have
 *  no path at all). Scales with footprint: a town centre is nearly a wall, a
 *  house barely deflects. Fields cost nothing (farmers walk straight onto them). */
export function buildingTileCost(defId: string): number {
  switch (defId) {
    case "town_center": return 9;
    case "barracks": return 6;
    case "temple": return 5.5;
    case "stables": return 5;
    case "storage_pit": return 2.2;
    case "house": return 2.2;
    case "field": return 0;
    default: return 4;
  }
}

/** Precomputed rampart geometry for one findPath call: a hub point per built land
 *  wall + a bar between each pair of adjacent land walls (the SAME thin-bar model the
 *  movement collision uses). `hits(seg)` answers "does this path segment cross a wall
 *  it can't physically pass?" — used so neither A* nor its smoothing ever plans a
 *  route straight THROUGH a rampart (the walk-into-the-wall bug). */
function buildWallBars(world: World, agentRadius: number, forPlayer?: number): {
  hits: (ax: number, ay: number, bx: number, by: number) => boolean;
  any: boolean;
} {
  const geometry = rampartGeometry(world);
  const padding = world.size * RAMPART_PATH_MARGIN_RATIO + agentRadius;
  const team = forPlayer === undefined ? undefined : world.players.get(forPlayer)?.team;
  return {
    hits: (ax, ay, bx, by) => rampartSegmentBlocked(geometry, ax, ay, bx, by, padding, team),
    any: geometry.hubs.length > 0 || geometry.arms.length > 0,
  };
}

interface HeapEntry { f: number; g: number; v: number }

class MinHeap {
  private a: HeapEntry[] = [];
  private less(a: HeapEntry, b: HeapEntry): boolean {
    return a.f < b.f || (a.f === b.f && (a.g < b.g || (a.g === b.g && a.v < b.v)));
  }
  get size() {
    return this.a.length;
  }
  push(f: number, g: number, v: number) {
    this.a.push({ f, g, v });
    let i = this.a.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (!this.less(this.a[i], this.a[p])) break;
      [this.a[p], this.a[i]] = [this.a[i], this.a[p]];
      i = p;
    }
  }
  pop(): HeapEntry | undefined {
    const top = this.a[0];
    const last = this.a.pop()!;
    if (this.a.length > 0) {
      this.a[0] = last;
      let i = 0;
      const n = this.a.length;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let s = i;
        if (l < n && this.less(this.a[l], this.a[s])) s = l;
        if (r < n && this.less(this.a[r], this.a[s])) s = r;
        if (s === i) break;
        [this.a[s], this.a[i]] = [this.a[i], this.a[s]];
        i = s;
      }
    }
    return top;
  }
}

/** Continuous terrain/domain sweep shared by planning and runtime movement.
 * Sampling is substantially finer than a hex inradius, so every crossed visual
 * terrain cell and elevation transition is validated in order. */
export function terrainSegmentClear(
  world: World,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  domain: MoveDomain,
  forPlayer?: number,
): boolean {
  const dx = bx - ax, dy = by - ay;
  const length = Math.hypot(dx, dy);
  const samples = Math.max(1, Math.ceil(length / (world.size * 0.18)));
  let previous: { col: number; row: number } | null = null;
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const hex = world.hexAtWorld(ax + dx * t, ay + dy * t);
    if (!hex || !world.passableFor(domain, hex.col, hex.row, forPlayer)) return false;
    if (domain === "land" && previous &&
      (previous.col !== hex.col || previous.row !== hex.row) &&
      !world.canClimb(previous.col, previous.row, hex.col, hex.row)) return false;
    previous = hex;
  }
  return true;
}

export function findPath(
  world: World,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  maxNodes = 6000,
  domain: MoveDomain = "land",
  forPlayer?: number,
  bestEffort = false,
  agentRadius = world.size * 0.18,
): Vec2[] | null {
  const start: Vec2 = { x: sx, y: sy };
  let requestedGoal: Vec2 = { x: tx, y: ty };
  let goalHex = world.hexAtWorld(tx, ty);
  if (!goalHex) {
    goalHex = world.hexes[world.hexes.length - 1];
    requestedGoal = { x: goalHex.x, y: goalHex.z };
  }
  if (!world.passableFor(domain, goalHex.col, goalHex.row, forPlayer)) {
    const nearest = nearestWalkableHex(world, goalHex.col, goalHex.row, 14, sx, sy, domain, forPlayer);
    if (!nearest) return null;
    requestedGoal = world.hexCenter(nearest[0], nearest[1]);
  }

  // This fixed Cartesian lattice is the navigation substrate. It is deliberately
  // unrelated to the visual hex centres; hexes are sampled only for terrain/domain
  // constraints. Lazy nodes keep the 150x90 map cheap, and final string-pulling
  // produces arbitrary-angle segments rather than eight-direction movement.
  const cell = world.size * 0.3;
  const minX = -world.size;
  const minY = -world.size;
  let maxX = minX, maxY = minY;
  for (const hex of world.hexes) {
    maxX = Math.max(maxX, hex.x + world.size);
    maxY = Math.max(maxY, hex.z + world.size);
  }
  const width = Math.ceil((maxX - minX) / cell) + 1;
  const height = Math.ceil((maxY - minY) / cell) + 1;
  const gridNodeCount = width * height;
  const moverTeam = forPlayer === undefined ? undefined : world.players.get(forPlayer)?.team;
  const portalPoints: Vec2[] = [];
  if (domain === "land" && moverTeam !== undefined) {
    for (const [key, owner] of [...world.gateHexKeys].sort((a, b) => a[0] - b[0])) {
      if (world.players.get(owner)?.team !== moverTeam) continue;
      const col = Math.floor(key / world.rows), row = key % world.rows;
      portalPoints.push(world.hexCenter(col, row));
    }
  }
  const walls = domain === "land" ? buildWallBars(world, agentRadius, forPlayer) : null;
  const propClearance = agentRadius + world.size * 0.02;

  const terrainClear = (a: Vec2, b: Vec2): boolean =>
    terrainSegmentClear(world, a.x, a.y, b.x, b.y, domain, forPlayer);

  const propsBlock = (a: Vec2, b: Vec2, allowStartOverlap: boolean): boolean => {
    if (domain === "naval") return false;
    for (const prop of propsAlong(world, a, b)) {
      const clearance = prop.r + propClearance;
      if (allowStartOverlap && Math.hypot(a.x - prop.x, a.y - prop.z) < clearance) continue;
      if (pointSegmentDistance(prop.x, prop.z, a.x, a.y, b.x, b.y) < clearance) return true;
    }
    return false;
  };

  const segmentClear = (a: Vec2, b: Vec2, allowStartOverlap = false): boolean =>
    terrainClear(a, b) &&
    (!walls?.any || !walls.hits(a.x, a.y, b.x, b.y)) &&
    !propsBlock(a, b, allowStartOverlap);

  const pointClear = (point: Vec2): boolean => segmentClear(point, point);
  const gridPoint = (ix: number, iy: number): Vec2 => ({ x: minX + ix * cell, y: minY + iy * cell });
  const gridKey = (ix: number, iy: number): number => ix * height + iy;
  const gridCoords = (key: number): [number, number] => [Math.floor(key / height), key % height];
  const pointOf = (key: number): Vec2 => {
    if (key >= gridNodeCount) return portalPoints[key - gridNodeCount];
    const [ix, iy] = gridCoords(key);
    return gridPoint(ix, iy);
  };

  const nearestGridNode = (
    point: Vec2,
    connectFrom: Vec2 | null,
    allowStartOverlap: boolean,
    preferFrom?: Vec2,
  ): number | null => {
    const baseX = Math.round((point.x - minX) / cell);
    const baseY = Math.round((point.y - minY) / cell);
    for (let ring = 0; ring <= 8; ring++) {
      let best = -1, bestDistance = Infinity, bestPreference = Infinity;
      for (let dx = -ring; dx <= ring; dx++) {
        for (let dy = -ring; dy <= ring; dy++) {
          if (ring > 0 && Math.abs(dx) !== ring && Math.abs(dy) !== ring) continue;
          const ix = baseX + dx, iy = baseY + dy;
          if (ix < 0 || iy < 0 || ix >= width || iy >= height) continue;
          const candidate = gridPoint(ix, iy);
          if (!pointClear(candidate) || (connectFrom && !segmentClear(connectFrom, candidate, allowStartOverlap))) continue;
          const distance = (candidate.x - point.x) ** 2 + (candidate.y - point.y) ** 2;
          const preference = preferFrom
            ? (candidate.x - preferFrom.x) ** 2 + (candidate.y - preferFrom.y) ** 2
            : 0;
          const key = gridKey(ix, iy);
          if (distance < bestDistance - 1e-9 ||
              (Math.abs(distance - bestDistance) <= 1e-9 &&
                (preference < bestPreference - 1e-9 ||
                  (Math.abs(preference - bestPreference) <= 1e-9 && key < best)))) {
            best = key;
            bestDistance = distance;
            bestPreference = preference;
          }
        }
      }
      if (best >= 0) return best;
    }
    return null;
  };

  // A click may land on the actual wall/building model. Resolve to the closest
  // continuous free point, preferring the side the unit approached from—not a
  // neighbouring visual tile centre.
  let resolvedGoal = requestedGoal;
  if (!pointClear(resolvedGoal)) {
    // Pick the closest free submesh point around the clicked footprint, breaking
    // symmetric ties toward the mover. Do not require direct visibility from the
    // start: A* still needs to route around any intervening wall to reach it.
    const safe = nearestGridNode(resolvedGoal, null, false, start);
    if (safe === null) return null;
    resolvedGoal = pointOf(safe);
  }
  if (segmentClear(start, resolvedGoal, true)) return [resolvedGoal];

  const startNode = nearestGridNode(start, start, true);
  const endNode = nearestGridNode(resolvedGoal, resolvedGoal, false);
  if (startNode === null || endNode === null) return null;

  const open = new MinHeap();
  const scores = new Map<number, number>([[startNode, 0]]);
  const parents = new Map<number, number>([[startNode, -1]]);
  const closed = new Set<number>();
  const destination = pointOf(endNode);
  const heuristic = (key: number): number => {
    const point = pointOf(key);
    return Math.hypot(point.x - destination.x, point.y - destination.y);
  };
  open.push(heuristic(startNode), 0, startNode);
  let bestNode = startNode;
  let bestDistance = heuristic(startNode);
  let visited = 0;
  const directions: readonly [number, number][] = [
    [1, 0], [1, 1], [0, 1], [-1, 1],
    [-1, 0], [-1, -1], [0, -1], [1, -1],
  ];

  while (open.size > 0 && visited < maxNodes) {
    const current = open.pop()!.v;
    if (closed.has(current)) continue;
    closed.add(current);
    visited++;
    if (current === endNode) break;
    const currentPoint = pointOf(current);
    const currentScore = scores.get(current)!;
    const distance = heuristic(current);
    if (distance < bestDistance || (distance === bestDistance && current < bestNode)) {
      bestDistance = distance;
      bestNode = current;
    }
    const consider = (next: number) => {
      if (next === current) return;
      if (closed.has(next)) return;
      const nextPoint = pointOf(next);
      // segmentClear already validates the destination point; checking it again as
      // a zero-length segment doubles the hottest geometry work in the search.
      if (!segmentClear(currentPoint, nextPoint)) return;

      const parent = current;
      const nextScore = currentScore + Math.hypot(nextPoint.x - currentPoint.x, nextPoint.y - currentPoint.y);
      const oldScore = scores.get(next) ?? Infinity;
      const oldParent = parents.get(next) ?? Infinity;
      if (nextScore < oldScore - 1e-9 || (Math.abs(nextScore - oldScore) <= 1e-9 && parent < oldParent)) {
        scores.set(next, nextScore);
        parents.set(next, parent);
        open.push(nextScore + heuristic(next), nextScore, next);
      }
    };
    if (current < gridNodeCount) {
      const [cx, cy] = gridCoords(current);
      for (const [dx, dy] of directions) {
        const nx = cx + dx, ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        consider(gridKey(nx, ny));
      }
    } else {
      // Exact portal centres augment the fixed lattice so a narrow gate remains
      // reachable at every orientation even when no lattice row happens to align
      // with its aperture. Connect only a small deterministic local neighbourhood.
      const radius = world.size * 1.25;
      const cells = Math.ceil(radius / cell);
      const baseX = Math.round((currentPoint.x - minX) / cell);
      const baseY = Math.round((currentPoint.y - minY) / cell);
      for (let dx = -cells; dx <= cells; dx++) {
        for (let dy = -cells; dy <= cells; dy++) {
          const nx = baseX + dx, ny = baseY + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const point = gridPoint(nx, ny);
          if (Math.hypot(point.x - currentPoint.x, point.y - currentPoint.y) <= radius) {
            consider(gridKey(nx, ny));
          }
        }
      }
    }
    const portalRadius = world.size * 1.25;
    for (let i = 0; i < portalPoints.length; i++) {
      const next = gridNodeCount + i;
      const point = portalPoints[i];
      if (Math.hypot(point.x - currentPoint.x, point.y - currentPoint.y) <= portalRadius) consider(next);
    }
  }

  const reached = closed.has(endNode);
  if (!reached && (!bestEffort || bestNode === startNode)) return null;
  const lastNode = reached ? endNode : bestNode;
  const reversed: Vec2[] = [];
  let cursor = lastNode;
  while (cursor !== startNode) {
    reversed.push(pointOf(cursor));
    const parent = parents.get(cursor);
    if (parent === undefined || parent < 0) return null;
    cursor = parent;
  }
  const raw = reversed.reverse();
  if (reached && (raw.length === 0 || Math.hypot(raw.at(-1)!.x - resolvedGoal.x, raw.at(-1)!.y - resolvedGoal.y) > 1e-6)) {
    raw.push(resolvedGoal);
  }

  // String-pull over the exact same continuous collision query. The lattice finds
  // topology; these shortcuts produce the requested 360-degree movement.
  const points = [start, ...raw];
  const path: Vec2[] = [];
  let index = 0;
  while (index < points.length - 1) {
    let next = points.length - 1;
    while (next > index + 1 && !segmentClear(points[index], points[next], index === 0)) next--;
    path.push(points[next]);
    index = next;
  }
  return path;
}

/** A prop's blocking footprint in world space: its real (off-centre) position
 *  plus the radius a unit's path must keep clear of it. */
interface PropCircle { x: number; z: number; r: number; }

/** Collect the prop footprint circles whose hexes the segment a→b passes through
 *  (plus their neighbours — a prop offset toward a tile edge can intrude into the
 *  segment while the sample point is technically in the next hex). */
function propsAlong(world: World, a: Vec2, b: Vec2): PropCircle[] {
  const dx = b.x - a.x, dz = b.y - a.y;
  const len = Math.hypot(dx, dz);
  const seen = new Set<number>();
  const out: PropCircle[] = [];
  const collect = (hk: number) => {
    if (seen.has(hk)) return;
    seen.add(hk);
    const p = world.propPos.get(hk);
    if (p) out.push({ x: p.x, z: p.z, r: p.r });
  };
  if (len <= 1e-9) {
    const h = world.hexAtWorld(a.x, a.y);
    if (h) {
      collect(world.hkey(h.col, h.row));
      for (const [nc, nr] of world.neighbors(h.col, h.row)) collect(world.hkey(nc, nr));
    }
    return out;
  }
  const steps = Math.max(1, Math.ceil(len / (world.size * 0.3)));
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    const h = world.hexAtWorld(a.x + dx * t, a.y + dz * t);
    if (!h) continue;
    collect(world.hkey(h.col, h.row));
    for (const [nc, nr] of world.neighbors(h.col, h.row)) collect(world.hkey(nc, nr));
  }
  return out;
}

/**
 * Nearest walkable hex within a radius, searched ring-by-ring outward from
 * (col,row). When (fromX,fromY) is given, the CLOSEST-to-goal ring that has any
 * walkable hex is chosen, and within it the hex nearest (fromX,fromY) wins — so
 * a unit approaches a blocked target (building) from ITS OWN side instead of
 * walking around to a fixed neighbor (which caused long zig-zag detours).
 */
export function nearestWalkableHex(
  world: World,
  col: number,
  row: number,
  radius: number,
  fromX?: number,
  fromY?: number,
  domain: MoveDomain = "land",
  forPlayer?: number
): [number, number] | null {
  const seen = new Set<number>([world.hkey(col, row)]);
  let frontier: [number, number][] = [[col, row]];
  const [oq, orA] = world.axial(col, row);
  for (let ring = 0; frontier.length && ring <= radius + 1; ring++) {
    let best: [number, number] | null = null;
    let bestD = Infinity;
    for (const [cc, cr] of frontier) {
      if (!world.passableFor(domain, cc, cr, forPlayer)) continue;
      // a WALL tile is passable TRANSIT (you skirt the rampart) but NOT a standing
      // DESTINATION — never pick the bar itself as a wander/snap/spawn target, or a
      // deer decides to stand ON the rampart and jams. (Its tile is the model, not
      // open ground.) Skip land walls; the surrounding open tiles are chosen instead.
      if (domain === "land" && world.solidHexKeys.has(world.hkey(cc, cr)) && !world.isWater(cc, cr)) continue;
      if (fromX === undefined || fromY === undefined) return [cc, cr];
      const ctr = world.hexCenter(cc, cr);
      const dd = (ctr.x - fromX) ** 2 + (ctr.y - fromY) ** 2;
      // lower-(col,row) tie-break on an exact distance tie keeps the pick
      // independent of frontier iteration order (matches every other nearest-X
      // scan in the sim — boardingShore/dockWater/nearestDropSite).
      if (dd < bestD || (dd === bestD && best !== null && (cc < best[0] || (cc === best[0] && cr < best[1])))) {
        bestD = dd; best = [cc, cr];
      }
    }
    if (best) return best; // nearest ring with a walkable hex, nearest to `from`
    const next: [number, number][] = [];
    for (const [cc, cr] of frontier) {
      const [cq, crA] = world.axial(cc, cr);
      if (hexDistance(oq, orA, cq, crA) > radius) continue;
      for (const nb of world.neighbors(cc, cr)) {
        const k = world.hkey(nb[0], nb[1]);
        if (seen.has(k)) continue;
        seen.add(k);
        next.push(nb);
      }
    }
    frontier = next;
  }
  return null;
}

/**
 * A point `gap` world-units from a target, on the side facing `from`. Use for
 * the final step toward a BLOCKED target (building/foundation): pathfinding can
 * only reach the adjacent tile, so the caller pathfinds while far and then
 * moves directly to this point to actually reach the target's own tile.
 */
export function approachPoint(
  fromX: number,
  fromY: number,
  tx: number,
  ty: number,
  gap: number
): Vec2 {
  const dx = fromX - tx;
  const dy = fromY - ty;
  const len = Math.hypot(dx, dy) || 1;
  return { x: tx + (dx / len) * gap, y: ty + (dy / len) * gap };
}

/** Nearest walkable hex to a WORLD point — returns its world center, or null. */
export function nearestWalkable(
  world: World,
  x: number,
  y: number,
  radius: number
): Vec2 | null {
  const h = world.hexAtWorld(x, y);
  if (!h) return null;
  const near = nearestWalkableHex(world, h.col, h.row, radius);
  if (!near) return null;
  return world.hexCenter(near[0], near[1]);
}
